// src/store/slices/vectorsSlice.ts
/**
 * Vectors Redux Slice
 * 
 * Manages vector arrows on the coordinate grid.
 * Phase 7: Vectors and Inequalities
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../types/redux';
import { Vector, VectorSum, Point2D } from '../../types/coordinateGrid';

// =============================================================================
// TYPES & INTERFACES
// =============================================================================

export interface VectorsState {
  vectors: Vector[];
  vectorSums: VectorSum[];
  selectedVectorIds: string[];
  nextId: number;
  /** Drawing state */
  drawingVector: {
    isDrawing: boolean;
    start: Point2D | null;
  } | null;
  /** Default styling for new vectors */
  defaultStyle: {
    color: string;
    thickness: number;
    showComponents: boolean;
    showMagnitude: boolean;
  };
}

// =============================================================================
// INITIAL STATE
// =============================================================================

const initialState: VectorsState = {
  vectors: [],
  vectorSums: [],
  selectedVectorIds: [],
  nextId: 1,
  drawingVector: null,
  defaultStyle: {
    color: '#3B82F6', // Blue
    thickness: 2,
    showComponents: false,
    showMagnitude: false,
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Calculate vector magnitude
 */
function calculateMagnitude(start: Point2D, end: Point2D): number {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Calculate resultant vector from multiple vectors
 */
function calculateResultant(vectors: Vector[]): { start: Point2D; end: Point2D } | null {
  if (vectors.length === 0) return null;
  
  // Start from the first vector's start point
  const start = { ...vectors[0].start };
  let x = 0;
  let y = 0;
  
  // Sum all vector components
  for (const vector of vectors) {
    x += vector.end.x - vector.start.x;
    y += vector.end.y - vector.start.y;
  }
  
  return {
    start,
    end: { x: start.x + x, y: start.y + y },
  };
}

// =============================================================================
// REDUX SLICE
// =============================================================================

export const vectorsSlice = createSlice({
  name: 'vectors',
  initialState,
  reducers: {
    /**
     * Add a new vector
     */
    addVector: (
      state,
      action: PayloadAction<{
        start: Point2D;
        end: Point2D;
        label?: string;
        style?: Partial<Vector>;
      }>
    ) => {
      const { start, end, label, style = {} } = action.payload;

      const newVector: Vector = {
        id: `vector-${state.nextId}`,
        start,
        end,
        label,
        showComponents: state.defaultStyle.showComponents,
        showMagnitude: state.defaultStyle.showMagnitude,
        color: state.defaultStyle.color,
        thickness: state.defaultStyle.thickness,
        isSelected: false,
        ...style,
      };

      state.vectors.push(newVector);
      state.nextId++;
    },

    /**
     * Update an existing vector
     */
    updateVector: (
      state,
      action: PayloadAction<{
        id: string;
        updates: Partial<Vector>;
      }>
    ) => {
      const vector = state.vectors.find((v) => v.id === action.payload.id);
      if (vector) {
        Object.assign(vector, action.payload.updates);
      }
    },

    /**
     * Delete a vector
     */
    deleteVector: (state, action: PayloadAction<string>) => {
      state.vectors = state.vectors.filter((v) => v.id !== action.payload);
      state.selectedVectorIds = state.selectedVectorIds.filter((id) => id !== action.payload);
      
      // Remove vector sums that reference this vector
      state.vectorSums = state.vectorSums.filter(
        (sum) => !sum.vectorIds.includes(action.payload)
      );
    },

    /**
     * Select a vector (with optional multi-select via Ctrl key)
     */
    selectVector: (
      state,
      action: PayloadAction<{
        id: string;
        ctrlKey?: boolean;
      }>
    ) => {
      const { id, ctrlKey = false } = action.payload;

      if (!ctrlKey) {
        // Deselect all vectors
        state.vectors.forEach((v) => {
          v.isSelected = false;
        });
        state.selectedVectorIds = [];
      }

      const vector = state.vectors.find((v) => v.id === id);
      if (vector) {
        vector.isSelected = !vector.isSelected;
        if (vector.isSelected) {
          if (!state.selectedVectorIds.includes(id)) {
            state.selectedVectorIds.push(id);
          }
        } else {
          state.selectedVectorIds = state.selectedVectorIds.filter((vid) => vid !== id);
        }
      }
    },

    /**
     * Deselect all vectors
     */
    deselectAllVectors: (state) => {
      state.vectors.forEach((v) => {
        v.isSelected = false;
      });
      state.selectedVectorIds = [];
    },

    /**
     * Toggle show components for a vector
     */
    toggleShowComponents: (state, action: PayloadAction<string>) => {
      const vector = state.vectors.find((v) => v.id === action.payload);
      if (vector) {
        vector.showComponents = !vector.showComponents;
      }
    },

    /**
     * Toggle show magnitude for a vector
     */
    toggleShowMagnitude: (state, action: PayloadAction<string>) => {
      const vector = state.vectors.find((v) => v.id === action.payload);
      if (vector) {
        vector.showMagnitude = !vector.showMagnitude;
      }
    },

    /**
     * Create a vector sum from selected vectors
     */
    createVectorSum: (
      state,
      action: PayloadAction<{
        vectorIds: string[];
        resultantColor?: string;
      }>
    ) => {
      const { vectorIds, resultantColor = '#EF4444' } = action.payload;

      // Get the actual vectors
      const vectors = state.vectors.filter((v) => vectorIds.includes(v.id));
      if (vectors.length === 0) return;

      const resultant = calculateResultant(vectors);
      if (!resultant) return;

      const newSum: VectorSum = {
        id: `sum-${state.nextId}`,
        vectorIds,
        resultant,
        showComponents: true,
        resultantColor,
      };

      state.vectorSums.push(newSum);
      state.nextId++;
    },

    /**
     * Update a vector sum
     */
    updateVectorSum: (
      state,
      action: PayloadAction<{
        id: string;
        updates: Partial<VectorSum>;
      }>
    ) => {
      const sum = state.vectorSums.find((s) => s.id === action.payload.id);
      if (sum) {
        Object.assign(sum, action.payload.updates);
        
        // Recalculate resultant if vector IDs changed
        if (action.payload.updates.vectorIds) {
          const vectors = state.vectors.filter((v) => sum.vectorIds.includes(v.id));
          const resultant = calculateResultant(vectors);
          if (resultant) {
            sum.resultant = resultant;
          }
        }
      }
    },

    /**
     * Delete a vector sum
     */
    deleteVectorSum: (state, action: PayloadAction<string>) => {
      state.vectorSums = state.vectorSums.filter((s) => s.id !== action.payload);
    },

    /**
     * Start drawing a vector
     */
    startDrawingVector: (state) => {
      state.drawingVector = {
        isDrawing: true,
        start: null,
      };
    },

    /**
     * Set the start point for drawing a vector
     */
    setVectorStart: (state, action: PayloadAction<Point2D>) => {
      if (state.drawingVector) {
        state.drawingVector.start = action.payload;
      }
    },

    /**
     * Complete drawing a vector
     */
    completeVector: (
      state,
      action: PayloadAction<{
        end: Point2D;
        label?: string;
      }>
    ) => {
      if (!state.drawingVector || !state.drawingVector.start) {
        return;
      }

      const { end, label } = action.payload;
      const newVector: Vector = {
        id: `vector-${state.nextId}`,
        start: state.drawingVector.start,
        end,
        label,
        showComponents: state.defaultStyle.showComponents,
        showMagnitude: state.defaultStyle.showMagnitude,
        color: state.defaultStyle.color,
        thickness: state.defaultStyle.thickness,
        isSelected: false,
      };

      state.vectors.push(newVector);
      state.nextId++;
      state.drawingVector = null;
    },

    /**
     * Cancel drawing a vector
     */
    cancelDrawing: (state) => {
      state.drawingVector = null;
    },

    /**
     * Load vectors state (for scene loading)
     */
    loadVectorsState: (state, action: PayloadAction<Partial<VectorsState>>) => {
      Object.assign(state, { ...initialState, ...action.payload });
    },

    /**
     * Clear all vectors
     */
    clearAllVectors: (state) => {
      state.vectors = [];
      state.vectorSums = [];
      state.selectedVectorIds = [];
    },
  },
});

// Export actions
export const {
  addVector,
  updateVector,
  deleteVector,
  selectVector,
  deselectAllVectors,
  toggleShowComponents,
  toggleShowMagnitude,
  createVectorSum,
  updateVectorSum,
  deleteVectorSum,
  startDrawingVector,
  setVectorStart,
  completeVector,
  cancelDrawing,
  loadVectorsState,
  clearAllVectors,
} = vectorsSlice.actions;

// Selectors
export const selectAllVectors = (state: RootState) => state.vectors.vectors;
export const selectVisibleVectors = (state: RootState) =>
  state.vectors.vectors; // All vectors are visible by default
export const selectSelectedVectors = (state: RootState) =>
  state.vectors.vectors.filter((v) => v.isSelected);
export const selectVectorSums = (state: RootState) => state.vectors.vectorSums;
export const selectDrawingVector = (state: RootState) => state.vectors.drawingVector;

export default vectorsSlice.reducer;
